import type { AqsiSaleOperation } from './types'

/**
 * RP-FB35 K7: видимость проверок. Окно хода чека под причиной одной мелкой строкой говорит, когда команда ушла в кабинет, когда его
 * последний раз читали, что он ответил и когда следующее чтение. Всё — из данных службы (`sentAt`, `checkedAt`, `nextCheckAt`,
 * `providerStatus`, `providerMessage`); время местное; смысл не только в цвете — это текст.
 */

const MINUTE = 60_000
const HOUR = 60 * MINUTE

/** Статус кабинета словами; незнакомый статус показывается как есть (дословно). */
const PROVIDER_STATUS_WORDS: Readonly<Record<string, string>> = {
  Pending: 'ждёт кассу',
  Processing: 'касса печатает',
  Finishing: 'касса завершает',
  Completed: 'исполнена',
  Error: 'ошибка',
  Timeout: 'срок команды истёк',
  Canceled: 'команда снята',
}

/** «Ждём N …» для больших чисел: минуты — до часа, часы — до трёх суток, дальше сутки. */
export function waitSpanText(ms: number): string {
  const minutes = Math.floor(ms / MINUTE)
  if (minutes < 60) return `${minutes} мин`
  const hours = Math.floor(ms / HOUR)
  if (hours < 72) return `${hours} ч`
  return `${Math.floor(hours / 24)} сут`
}

function parse(iso: string | null): number | null {
  if (iso === null || iso === '') return null
  const value = Date.parse(iso)
  return Number.isNaN(value) ? null : value
}

/** Местное время; чужие сутки - с датой («17.10 14:02»), секунды - только если просят. */
function momentText(value: number, now: number, seconds: boolean): string {
  const moment = new Date(value)
  const time = moment.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}) })
  const sameDay = new Date(now).toDateString() === moment.toDateString()
  return sameDay ? time : `${moment.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })} ${time}`
}

/** «через 40 с», «через 4 мин», «через 2 ч». */
function inText(ms: number): string {
  if (ms < MINUTE) return `через ${Math.max(1, Math.ceil(ms / 1000))} с`
  if (ms < HOUR) return `через ${Math.ceil(ms / MINUTE)} мин`
  return `через ${Math.ceil(ms / HOUR)} ч`
}

/** Просрочка чтения больше этого — службе не удаётся прочитать кабинет (недоступен), и окно говорит об этом. */
const OVERDUE_MS = 2 * MINUTE

/**
 * Строка проверок или `null`, если рассказывать нечего (команда не уходила в кабинет). Ничего не выдумывает: нет `checkedAt` — «кабинет
 * ещё не читали»; следующее чтение просрочено — «проверка задерживается».
 */
export function receiptWatchNote(operation: AqsiSaleOperation | undefined, now: number): string | null {
  if (operation === undefined) return null
  const sent = parse(operation.sentAt)
  if (sent === null) return null
  const parts = [`Команда ушла в кабинет в ${momentText(sent, now, false)}`]
  const checked = parse(operation.checkedAt)
  if (checked === null) parts.push('кабинет ещё не читали')
  else {
    const status = operation.providerStatus
    const answer =
      status === null
        ? 'ответа кабинета нет'
        : `кабинет — ${PROVIDER_STATUS_WORDS[status] ?? status}${operation.providerMessage !== null && operation.providerMessage !== '' ? ` («${operation.providerMessage}»)` : ''}`
    parts.push(`проверено в ${momentText(checked, now, true)}: ${answer}`)
  }

  const unresolved = operation.state === 'Pending' || operation.state === 'Sent' || operation.state === 'Unknown'
  const next = parse(operation.nextCheckAt)
  if (unresolved && next !== null) {
    if (now - next > OVERDUE_MS) parts.push('следующая проверка задерживается — кабинет или служба недоступны')
    else if (next <= now) parts.push('ожидаем следующую проверку')
    else parts.push(`следующая проверка ${inText(next - now)}`)
  }

  return parts.join(' · ')
}
